import { showToast } from '@/features/toasts/toastsSlice';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { ChartBar, Share2 } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { AudienceChip } from './components/AudienceChip';
import { ChartMetric, TrendChart } from './components/TrendChart';
import { ComparisonTable } from './components/ComparisonTable';
import { DateRangeChip } from './components/DateRangeChip';
import { GoogleAccountsChip } from './components/GoogleAccountsChip';
import { KpiHero } from './components/KpiHero';
import { MetaAccountsChip } from './components/MetaAccountsChip';
import { StoreVisitsCard } from './components/StoreVisitsCard';
import { Audience, DashboardResponse, Platform, Source } from './mock/types';
import { computeDefaultRange, getAudiences, getDashboardData, getSources } from './mock/api.mock';
import { DEFAULT_GOOGLE_IDS, DEFAULT_META_IDS } from './mock/sources.mock';
import { DEFAULT_AUDIENCE_ID } from './mock/audiences.mock';
import { STORE_VISITS_BY_LOCATION } from './mock/dashboard.mock';
import { fmtInt } from './utils';

const METRIC_BUTTONS: { key: ChartMetric; label: string }[] = [
  { key: 'impression', label: 'Impression' },
  { key: 'click', label: 'Click' },
  { key: 'costo', label: 'Costo' },
  { key: 'ctr', label: 'CTR' },
];

export const CampaignPerformancePage: FC = () => {
  const dispatch = useAppDispatch();

  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);

  const [audienceId, setAudienceId] = useState<string>(DEFAULT_AUDIENCE_ID);
  const [datePreset, setDatePreset] = useState<string>('Ultimi 7 giorni');
  const [googleIds, setGoogleIds] = useState<string[]>(DEFAULT_GOOGLE_IDS);
  const [metaIds, setMetaIds] = useState<string[]>(DEFAULT_META_IDS);
  const [audiencePopOpen, setAudiencePopOpen] = useState(false);
  const [metric, setMetric] = useState<ChartMetric>('impression');

  useEffect(() => {
    getAudiences().then(setAudiences);
    getSources().then((r) => setSources(r.sources));
  }, []);

  useEffect(() => {
    const { from, to } = computeDefaultRange(datePreset);
    getDashboardData({
      audienceId,
      from,
      to,
      googleAccountIds: googleIds,
      metaAccountIds: metaIds,
    }).then(setDashboard);
  }, [audienceId, datePreset, googleIds, metaIds]);

  const selectedAudience = audiences.find((a) => a.id === audienceId);

  const visiblePlatforms = useMemo<Platform[]>(() => {
    const out: Platform[] = [];
    if (googleIds.length > 0) out.push('google');
    if (metaIds.length > 0) out.push('meta');
    return out;
  }, [googleIds, metaIds]);

  const toastInfo = (message: string) => dispatch(showToast({ type: ToastType.SUCCESS, title: '', message }));
  const toastNeutral = (message: string) => dispatch(showToast({ type: ToastType.INFO, title: '', message }));

  const handleExport = () => toastInfo("Esportazione avviata · Riceverai un'email al termine");
  const handleExportCsv = () => toastInfo("Esportazione avviata · Riceverai un'email al termine");
  const handleStoreVisitsInfo = () => toastNeutral('Apertura documentazione Google · Store Visits');

  const audienceUsers = selectedAudience?.usersCount ?? 0;
  const googlePlatform = dashboard?.platforms.find((p) => p.platform === 'google');
  const metaPlatform = dashboard?.platforms.find((p) => p.platform === 'meta');
  const campaignCountGoogle = googlePlatform?.campaignCount ?? 0;
  const adSetCountMeta = metaPlatform?.adGroupCount ?? 0;
  const totalStoreVisits = googlePlatform?.storeVisits ?? 0;

  const scaledLocations = useMemo(() => {
    // Scale per-location visits proportionally to current Google storeVisits vs baseline 240
    if (!googlePlatform || googlePlatform.storeVisits === null) return STORE_VISITS_BY_LOCATION;
    const baselineTotal = STORE_VISITS_BY_LOCATION.reduce((s, l) => s + l.visits, 0);
    if (baselineTotal === 0) return STORE_VISITS_BY_LOCATION;
    const ratio = googlePlatform.storeVisits / baselineTotal;
    return STORE_VISITS_BY_LOCATION.map((l) => ({ ...l, visits: Math.max(0, Math.round(l.visits * ratio)) }));
  }, [googlePlatform]);

  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, "Helvetica Neue", system-ui, sans-serif',
        color: '#0B1733',
        background: '#FFFFFF',
        padding: '32px 40px 60px',
        letterSpacing: '-0.005em',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: '#E8EFFF',
            color: '#1452E6',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <ChartBar size={20} color='currentColor' />
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0B1733', letterSpacing: '-0.01em' }}>Performance Audience</div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12.5,
            color: '#7A8597',
            marginLeft: 4,
          }}
        >
          <span style={{ color: '#C6CBD4' }}>·</span>
          <span>Aggiornata 2 min fa</span>
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type='button'
            onClick={handleExport}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#FFFFFF',
              color: '#0B1733',
              border: '1px solid #DEE2E8',
              padding: '9px 14px',
              fontSize: 13,
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >
            <Share2 size={14} color='currentColor' />
            <span>Esporta</span>
          </button>
        </div>
      </div>

      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E7EAEF',
          borderRadius: 18,
          padding: 26,
          boxShadow: '0 1px 2px rgba(11,23,51,0.04), 0 4px 16px rgba(11,23,51,0.04)',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        {/* Filter chips row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <AudienceChip audiences={audiences} selectedId={audienceId} onChange={setAudienceId} isOpen={audiencePopOpen} onOpenChange={setAudiencePopOpen} />
          <GoogleAccountsChip sources={sources} selectedIds={googleIds} onChange={setGoogleIds} />
          <MetaAccountsChip sources={sources} selectedIds={metaIds} onChange={setMetaIds} />
          <DateRangeChip value={datePreset} onChange={setDatePreset} />
        </div>

        {/* Scope status line */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12.5,
            color: '#58657B',
            padding: '0 2px',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: '#16955E',
              boxShadow: '0 0 0 3px #DDF3E7',
              display: 'inline-block',
            }}
          />
          <span>Stai visualizzando</span>
          <b style={{ color: '#0B1733', fontWeight: 600 }}>{googleIds.length} account Google</b>
          <span style={{ color: '#C6CBD4' }}>+</span>
          <b style={{ color: '#0B1733', fontWeight: 600 }}>{metaIds.length} ad account Meta</b>
          <span style={{ color: '#C6CBD4' }}>·</span>
          <span>
            Audience <b style={{ color: '#0B1733', fontWeight: 600 }}>{selectedAudience?.name ?? '—'}</b>
            {selectedAudience ? ` (${fmtInt(selectedAudience.usersCount)} utenti)` : ''}
          </span>
        </div>

        {/* Context sentence */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E7EAEF',
            borderRadius: 14,
            padding: '14px 18px',
            fontSize: 13.5,
            color: '#344155',
            lineHeight: 1.55,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              background: '#E8EFFF',
              borderRadius: 8,
              display: 'grid',
              placeItems: 'center',
              color: '#1452E6',
              flex: '0 0 30px',
            }}
          >
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M12 3l1.7 5.2L19 10l-5.3 1.8L12 17l-1.7-5.2L5 10l5.3-1.8L12 3z' />
            </svg>
          </div>
          <div>
            Stai visualizzando le performance dell'audience <b style={{ color: '#0B1733' }}>{selectedAudience?.name ?? '—'}</b> (
            <b style={{ color: '#0B1733' }}>{selectedAudience ? fmtInt(selectedAudience.usersCount) : '—'} utenti</b>) attivata su{' '}
            <b style={{ color: '#0B1733' }}>
              {campaignCountGoogle} {campaignCountGoogle === 1 ? 'campagna' : 'campagne'} Google Ads
            </b>{' '}
            e{' '}
            <b style={{ color: '#0B1733' }}>
              {adSetCountMeta} {adSetCountMeta === 1 ? 'ad set' : 'ad set'} Meta
            </b>{' '}
            nel periodo selezionato.{' '}
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault();
                setAudiencePopOpen(true);
              }}
              style={{ color: '#1452E6', textDecoration: 'none', fontWeight: 500 }}
            >
              Modifica scope →
            </a>
          </div>
        </div>

        {/* KPI hero strip */}
        <KpiHero audienceUsers={audienceUsers} platforms={dashboard?.platforms ?? []} />

        {/* Two-column row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: 18,
          }}
          className='cp-perf-row'
        >
          {/* Trend chart */}
          <div style={cardStyle}>
            <div style={cardHeadStyle}>
              <div>
                <div style={cardTitleStyle}>Andamento nel periodo</div>
                <div style={cardSubStyle}>Confronto giornaliero tra Google Ads e Meta.</div>
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  background: '#F0F2F5',
                  borderRadius: 9,
                  padding: 3,
                  gap: 2,
                }}
              >
                {METRIC_BUTTONS.map((m) => {
                  const active = metric === m.key;
                  return (
                    <button
                      key={m.key}
                      type='button'
                      onClick={() => setMetric(m.key)}
                      style={{
                        border: 0,
                        background: active ? '#FFFFFF' : 'transparent',
                        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                        fontSize: 12,
                        fontWeight: active ? 600 : 500,
                        color: active ? '#0B1733' : '#58657B',
                        padding: '6px 11px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                justifyContent: 'flex-start',
                gap: 18,
                marginBottom: 6,
              }}
            >
              {visiblePlatforms.includes('google') ? (
                <span style={legendItemStyle}>
                  <span style={{ width: 18, height: 3, borderRadius: 2, background: '#1452E6', display: 'inline-block' }} />
                  Google Ads
                </span>
              ) : null}
              {visiblePlatforms.includes('meta') ? (
                <span style={legendItemStyle}>
                  <span
                    style={{
                      width: 18,
                      height: 3,
                      borderRadius: 2,
                      backgroundImage: 'repeating-linear-gradient(90deg,#0B1733 0 4px,transparent 4px 7px)',
                      display: 'inline-block',
                    }}
                  />
                  Meta
                </span>
              ) : null}
            </div>
            {dashboard ? (
              <TrendChart daily={dashboard.daily} metric={metric} visiblePlatforms={visiblePlatforms} />
            ) : (
              <div style={{ height: 240, display: 'grid', placeItems: 'center', color: '#9CA3B1', fontSize: 13 }}>Caricamento…</div>
            )}
          </div>

          {/* Comparison table */}
          <div style={cardStyle}>
            <div style={cardHeadStyle}>
              <div>
                <div style={cardTitleStyle}>Confronto piattaforme</div>
                <div style={cardSubStyle}>Metriche raccolte dalle piattaforme native, riferite all'audience selezionata.</div>
              </div>
              <button
                type='button'
                onClick={handleExportCsv}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#FFFFFF',
                  color: '#0B1733',
                  border: '1px solid #DEE2E8',
                  padding: '7px 11px',
                  fontSize: 12,
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                }}
              >
                <Share2 size={12} color='currentColor' />
                <span>Esporta CSV</span>
              </button>
            </div>
            <ComparisonTable platforms={dashboard?.platforms ?? []} visiblePlatforms={visiblePlatforms} />
          </div>
        </div>

        {/* Store visits expansion card — only when the active Google account has Business Profile locations */}
        {visiblePlatforms.includes('google') && scaledLocations.length > 0 && googlePlatform?.storeVisits !== null ? (
          <StoreVisitsCard totalVisits={totalStoreVisits} locations={scaledLocations} onMoreInfo={handleStoreVisitsInfo} />
        ) : null}
      </div>
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #E7EAEF',
  borderRadius: 14,
  padding: '22px 26px 24px',
};

const cardHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 14,
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: '#0B1733',
  letterSpacing: '-0.01em',
};

const cardSubStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: '#7A8597',
  marginTop: 2,
};

const legendItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13.5,
  color: '#1B2538',
};
